import { Fragment, FunctionComponent, useCallback, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";

import {
	faChevronDown,
	faChevronRight,
	faMagnifyingGlass,
	faPlus,
	faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal, LoadingOverlay } from "@/components";
import { Toggle } from "@/components/Toggle/Toggle";
import { InputField } from "@/components/InputField/InputField";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";
import { AutoComplete, AutoCompleteItem as ACItem } from "@/components/AutoComplete/AutoComplete";

import {
	useGetAdminFeaturesQuery,
	useToggleFeatureMutation,
	useDeleteFeatureMutation,
} from "@/pages/Private/redux/feature/admin-feature.api";
import { useLazyGetCompaniesQuery } from "@/pages/Private/redux/companies/companies.api";

import { AdminFeature } from "../schema/admin-feature";
import { AddOverrideModal } from "./AddOverrideModal";

interface FeatureGroup {
	featureCode: string;
	global: AdminFeature | null;
	overrides: AdminFeature[];
}

export const FeatureManagementTable: FunctionComponent = () => {
	const { data: features, isLoading, isFetching } = useGetAdminFeaturesQuery();
	const [toggleFeature] = useToggleFeatureMutation();
	const [deleteFeature, { isLoading: isDeleting }] = useDeleteFeatureMutation();

	const [searchValue, setSearchValue] = useState("");
	const [showAddModal, setShowAddModal] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<AdminFeature | null>(null);
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	// Company filter
	const [filterCompanyId, setFilterCompanyId] = useState<number | null>(null);
	const [selectedCompanyName, setSelectedCompanyName] = useState("");
	const [triggerGetCompanies, { data: companiesData, isFetching: isCompaniesLoading }] =
		useLazyGetCompaniesQuery();

	const companyOptions: ACItem[] = useMemo(
		() =>
			companiesData?.data.map((company) => ({
				id: company.id,
				title: company.name,
			})) || [],
		[companiesData]
	);

	const toggleRow = useCallback((featureCode: string) => {
		setExpandedRows((prev) => {
			const next = new Set(prev);

			if (next.has(featureCode)) {
				next.delete(featureCode);
			} else {
				next.add(featureCode);
			}

			return next;
		});
	}, []);

	const grouped = useMemo<FeatureGroup[]>(() => {
		if (!features) {
			return [];
		}

		const map = new Map<string, FeatureGroup>();

		for (const f of features) {
			if (!map.has(f.feature)) {
				map.set(f.feature, { featureCode: f.feature, global: null, overrides: [] });
			}

			const group = map.get(f.feature)!;

			if (f.companyId === null) {
				group.global = f;
			} else {
				group.overrides.push(f);
			}
		}

		const result = Array.from(map.values());

		result.sort((a, b) => a.featureCode.localeCompare(b.featureCode));

		return result;
	}, [features]);

	const filtered = useMemo(() => {
		let result = grouped;

		if (searchValue.trim()) {
			const q = searchValue.toLowerCase();

			result = result.filter((g) => g.featureCode.toLowerCase().includes(q));
		}

		if (filterCompanyId !== null) {
			result = result.filter((g) => g.overrides.some((o) => o.companyId === filterCompanyId));
		}

		return result;
	}, [grouped, searchValue, filterCompanyId]);

	const handleToggle = async (id: number, currentActive: boolean) => {
		await toggleFeature({ id, active: !currentActive });
	};

	const handleDelete = async () => {
		if (!deleteTarget) {
			return;
		}

		await deleteFeature(deleteTarget.id);
		setDeleteTarget(null);
	};

	if (isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<div>
			<div className="flex flex-row gap-4 mb-6 items-center">
				<div className="w-64">
					<InputField
						className="-mb-3"
						handleChange={(e) => setSearchValue(e.target.value)}
						iconInside={<Icon className="text-gray-500" icon={faMagnifyingGlass} />}
						name="search"
						placeholder="Search by feature code..."
						value={searchValue}
					/>
				</div>
				<div className="w-64">
					<AutoComplete
						data={companyOptions}
						handleReset={() => {
							setSelectedCompanyName("");
							setFilterCompanyId(null);
						}}
						handleSearch={(value) => {
							triggerGetCompanies({ page: 1, limit: 20, searchValue: value });
						}}
						handleSelect={(item) => {
							if (item) {
								setFilterCompanyId(item.id as number);
								setSelectedCompanyName(item.title);
							}
						}}
						isLoading={isCompaniesLoading}
						placeholder="Filter by company..."
						selectedItem={selectedCompanyName}
					/>
				</div>
				<div className="w-64">
					<Button
						className="w-full"
						color={ButtonColor.ACTION}
						size={ButtonSize.ML}
						image={<Icon icon={faPlus} />}
						title="Add Company Override"
						onClick={() => setShowAddModal(true)}
					/>
				</div>
			</div>

			<div className="overflow-x-auto w-full">
				<table className="w-full text-left border-separate border-white">
					<thead>
						<tr>
							<th className="px-3 py-3 text-xs font-medium text-table-header w-8" />
							<th className="px-3 py-3 text-xs font-medium text-table-header">Feature Code</th>
							<th className="px-3 py-3 text-xs font-medium text-table-header">Global</th>
							<th className="px-3 py-3 text-xs font-medium text-table-header">Overrides</th>
							<th className="px-3 py-3 text-xs font-medium text-table-header">Updated</th>
						</tr>
					</thead>
					<tbody>
						{filtered.length === 0 ? (
							<tr>
								<td className="px-3 py-8 text-center text-sm text-gray-500" colSpan={5}>
									No features found
								</td>
							</tr>
						) : (
							filtered.map((group) => (
								<FeatureGroupRows
									key={group.featureCode}
									expandedRows={expandedRows}
									group={group}
									isFetching={isFetching}
									onDelete={setDeleteTarget}
									onToggle={handleToggle}
									onToggleRow={toggleRow}
								/>
							))
						)}
					</tbody>
				</table>
			</div>

			{showAddModal && <AddOverrideModal isOpen={showAddModal} setIsOpen={setShowAddModal} />}

			{deleteTarget && (
				<Modal
					handleClose={() => setDeleteTarget(null)}
					handleSave={handleDelete}
					isLoading={isDeleting}
					isOpened={!!deleteTarget}
					submitButtonColor="error"
					submitButtonText="Delete"
					title="Delete Company Override"
				>
					<p className="text-sm text-text-meta">
						Are you sure you want to delete the override for{" "}
						<span className="font-semibold">{deleteTarget.feature}</span>
						{deleteTarget.company
							? ` (${deleteTarget.company.name})`
							: ` (Company ID: ${deleteTarget.companyId})`}
						?
					</p>
				</Modal>
			)}
		</div>
	);
};

const getOverrideSummary = (overrides: AdminFeature[]): string => {
	if (overrides.length === 0) {
		return "No overrides";
	}

	const active = overrides.filter((o) => o.active).length;
	const inactive = overrides.length - active;

	const parts: string[] = [];

	if (active > 0) {
		parts.push(`${active} active`);
	}

	if (inactive > 0) {
		parts.push(`${inactive} inactive`);
	}

	return `${overrides.length} override${overrides.length !== 1 ? "s" : ""} (${parts.join(", ")})`;
};

interface FeatureGroupRowsProps {
	group: FeatureGroup;
	isFetching: boolean;
	expandedRows: Set<string>;
	onToggle: (id: number, active: boolean) => void;
	onDelete: (feature: AdminFeature) => void;
	onToggleRow: (featureCode: string) => void;
}

const FeatureGroupRows: FunctionComponent<FeatureGroupRowsProps> = ({
	group,
	isFetching,
	expandedRows,
	onToggle,
	onDelete,
	onToggleRow,
}) => {
	const isExpanded = expandedRows.has(group.featureCode);
	const hasOverrides = group.overrides.length > 0;

	return (
		<Fragment>
			{/* Summary row */}
			<tr
				className={classNames(
					"bg-brand-white hover:bg-blue-action-200 transition-colors",
					hasOverrides ? "cursor-pointer" : "",
					isFetching ? "opacity-60" : ""
				)}
				onClick={() => hasOverrides && onToggleRow(group.featureCode)}
			>
				<td className="px-3 py-3 text-sm w-8">
					{hasOverrides && (
						<Icon
							className="text-gray-500"
							icon={isExpanded ? faChevronDown : faChevronRight}
							size="sm"
						/>
					)}
				</td>
				<td className="px-3 py-3 text-sm font-medium">{group.featureCode}</td>
				<td className="px-3 py-3">
					{group.global ? (
						<Toggle
							isChecked={group.global.active}
							onChange={(e) => {
								e.stopPropagation();
								onToggle(group.global!.id, group.global!.active);
							}}
						/>
					) : (
						<span className="text-xs text-text-meta">—</span>
					)}
				</td>
				<td className="px-3 py-3 text-sm text-text-meta">{getOverrideSummary(group.overrides)}</td>
				<td className="px-3 py-3 text-xs text-text-meta">
					{group.global
						? formatDistanceToNow(new Date(group.global.updatedAt), { addSuffix: true })
						: "—"}
				</td>
			</tr>

			{/* Expanded override rows */}
			{isExpanded &&
				group.overrides.map((feature) => (
					<tr
						key={feature.id}
						className={classNames(
							"bg-brand-white hover:bg-blue-action-200 transition-colors",
							isFetching ? "opacity-60" : ""
						)}
					>
						<td className="px-3 py-3" />
						<td className="px-3 py-3 text-sm pl-8">
							<span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-text-meta">
								{feature.company ? feature.company.name : `Company ${feature.companyId}`}
							</span>
						</td>
						<td className="px-3 py-3">
							<Toggle
								isChecked={feature.active}
								onChange={() => onToggle(feature.id, feature.active)}
							/>
						</td>
						<td className="px-3 py-3" />
						<td className="px-3 py-3 text-xs text-text-meta">
							<div className="flex items-center justify-between">
								<span>{formatDistanceToNow(new Date(feature.updatedAt), { addSuffix: true })}</span>
								<button
									className="text-gray-500 hover:text-status-error cursor-pointer transition-colors ml-3"
									onClick={(e) => {
										e.stopPropagation();
										onDelete(feature);
									}}
								>
									<Icon icon={faTrashCan} />
								</button>
							</div>
						</td>
					</tr>
				))}
		</Fragment>
	);
};
